import './BuyerDocumentExecutionForm.scss';
import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Label } from 'reactstrap';
import { Feedback, Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import { useStateSelector } from '../../../../store/selectors';
import { MarketplaceDocumentSectionFieldValidator } from '../DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../../slices/marketplace/timeline/models/document-details.model';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { EngagementDocumentSource } from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import {
    ActionDocumentType,
    DocumentExecutionTypeOptionsMapping,
} from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';

interface BuyerDocumentExecutionFormProps {
    party: TimelineParty;
    onSubmit(action: TimelineAction, documentDetails?: DocumentDetails): void;
    disabled: boolean;
    documentDetails?: DocumentDetails;
    documentDetailsList: DocumentDetails[];
    documentsSections: DocumentSection[];
    setDocumentSectionList(sections: DocumentSection[]): void;
}

export const BuyerDocumentExecutionForm = forwardRef(
    (props: BuyerDocumentExecutionFormProps, ref) => {
        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const documentTemplates = useStateSelector(
            (state) => state.timelines.documentTemplatesTable.templates
        );

        const [documentDetails, setDocumentDetails] = useState({
            ...(props.documentDetails ??
                createDefaultDocumentDetails(
                    true,
                    undefined,
                    ActionDocumentType.ForSign,
                    undefined,
                    EngagementDocumentSource.Template,
                    TimelineParty.VetValue
                )),
        });

        return (
            <div className="buyer-documnent-execution-form">
                <Form
                    innerRef={formRef}
                    initialValues={{
                        actionType: TimelineActionType.BuyerDocumentExecution,
                        documentExecutionType: documentDetails.type,
                        documentTemplate: documentDetails.templateId,
                        documentName: documentDetails.name,
                        documentSection: documentDetails.sectionId,
                    }}
                    validationSchema={yup.object().shape({
                        documentExecutionType: yup
                            .number()
                            .min(0, 'Document Execution Type is required'),
                        documentTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        documentSection:
                            MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                props.documentsSections,
                                props.party
                            ),
                        documentName: yup
                            .string()
                            .trim()
                            .required('Document name is required'),
                    })}
                    onSubmit={async (values) => {
                        let docDetails = {
                            ...documentDetails,
                            name: values.documentName.trim(),
                            type: values.documentExecutionType,
                            templateId: values.documentTemplate,
                            sectionId: values.documentSection,
                        };

                        props.onSubmit(
                            {
                                ...defaultTimelineAction,
                                actionType: Number(
                                    TimelineActionType.BuyerDocumentExecution
                                ),
                                referenceItemId: docDetails.id,
                            },
                            docDetails
                        );
                    }}>
                    <div className="document-execution">
                        <Label for="documentExecutionType">Action Type</Label>
                        <Input
                            type="select"
                            name="documentExecutionType"
                            disabled={props.disabled}>
                            <option value={-1}>Select</option>
                            {DocumentExecutionTypeOptionsMapping.map(
                                (option) => (
                                    <option
                                        key={`type-${option.value}`}
                                        value={option.value}>
                                        {option.label}
                                    </option>
                                )
                            )}
                        </Input>
                        <Feedback name="documentExecutionType" />

                        <Label for="documentTemplate">Document Template</Label>
                        <Input
                            type="select"
                            name="documentTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="documentTemplate" />
                        <MarketplaceDocumentSectionField
                            documentsSections={props.documentsSections}
                            disabled={props.disabled}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                            type={'buyers'}
                            sectionsModalMode={'built-in'}
                        />
                        <Field
                            className="field"
                            name="documentName"
                            label="Document Name"
                            disabled={props.disabled}
                        />
                    </div>
                </Form>
            </div>
        );
    }
);
